import React, { useCallback, useMemo, useState } from 'react';
import { Button, Col, Form, Row, Spinner, Alert, Card, Pagination } from 'react-bootstrap';
import { FaFileExcel, FaSearch } from 'react-icons/fa';
import MaskedInput from 'react-text-mask';
import DateMask from '~/Components/DateMask';
import * as constants from '~/constant';
import { relatorioGestaoRecisoes } from '~/Services/Service';
import { createDate, salvarEmExcel } from '~/Utils/Helpers';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import Header from '~/Components/Header/Header';
import './style.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const ITEMS_PER_PAGE = 15;

export default function GestaoRescisoes() {
  const [dataInicial, setDataPagamentoInicial] = useState('');
  const [dataFinal, setDataPagamentoFinal] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const [error, setError] = useState(null);
  const [dados, setDados] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const isValidDate = useCallback(str => {
    if (!str) return false;
    const numeric = str.replace(/\D/g, '');
    if (numeric.length !== 8) return false;
    const d = createDate(str, constants.DATE_DEFAULT_FORMAT);
    return d.isValid();
  }, []);

  const pagOrderInvalid = useMemo(() => {
    if (!dataInicial || !dataFinal) return false;
    if (!isValidDate(dataInicial) || !isValidDate(dataFinal)) return false;
    const ini = createDate(dataInicial, constants.DATE_DEFAULT_FORMAT);
    const fin = createDate(dataFinal, constants.DATE_DEFAULT_FORMAT);
    return ini.isAfter(fin);
  }, [dataInicial, dataFinal, isValidDate]);

  const dataPagamentoInicialError = useMemo(() => {
    if (!dataInicial) return null;
    if (!isValidDate(dataInicial)) return 'Data inválida';
    if (pagOrderInvalid) return 'Data inicial não pode ser maior que a final';
    return null;
  }, [dataInicial, pagOrderInvalid, isValidDate]);

  const dataPagamentoFinalError = useMemo(() => {
    if (!dataFinal) return null;
    if (!isValidDate(dataFinal)) return 'Data inválida';
    if (pagOrderInvalid) return 'Data inicial não pode ser maior que a final';
    return null;
  }, [dataFinal, pagOrderInvalid, isValidDate]);

  const validPagPair = useMemo(() => {
    return (
      !!dataInicial &&
      !!dataFinal &&
      isValidDate(dataInicial) &&
      isValidDate(dataFinal) &&
      !pagOrderInvalid
    );
  }, [dataInicial, dataFinal, isValidDate, pagOrderInvalid]);

  const hasDateError = !!dataPagamentoInicialError || !!dataPagamentoFinalError;

  const canSearch = useMemo(() => {
    return validPagPair && !hasDateError;
  }, [validPagPair, hasDateError]);

  const totalPago = useMemo(() => {
    if (!dados || !dados.length) return 0;
    return dados.reduce((acc, item) => acc + (item.valor_pago_periodo || 0), 0);
  }, [dados]);

  const totalAPagar = useMemo(() => {
    if (!dados || !dados.length) return 0;
    return dados.reduce(
      (acc, item) => acc + (item.valor_a_pagar_periodo || 0),
      0
    );
  }, [dados]);

  const totalPages = useMemo(() => {
    return Math.ceil(dados.length / ITEMS_PER_PAGE);
  }, [dados]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return dados.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [dados, currentPage]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const pieData = useMemo(
    () => ({
      labels: ['A pagar', 'Pagos'],
      datasets: [
        {
          data: [totalAPagar, totalPago],
          backgroundColor: [
            'rgba(255, 159, 64, 0.7)',
            'rgba(255, 99, 132, 0.7)',
          ],
          borderColor: ['rgba(255, 159, 64, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1,
        },
      ],
    }),
    [totalAPagar, totalPago]
  );

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: context => {
              const label = context.label || '';
              const value = context.parsed || 0;
              const soma = totalAPagar + totalPago;
              const perc =
                soma > 0 ? ((value / soma) * 100).toFixed(2) + '%' : '0%';
              return `${label}: ${new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(value)} (${perc})`;
            },
          },
        },
      },
    }),
    [totalAPagar, totalPago]
  );

  const handleSearch = useCallback(async () => {
    const toServerDate = str => {
      if (!str) return null;
      const d = createDate(str, constants.DATE_DEFAULT_FORMAT);
      return d.isValid() ? d.format(constants.DATE_FORMAT_SERVER) : null;
    };

    const payload = {
      data_inicial: toServerDate(dataInicial),
      data_final: toServerDate(dataFinal),
    };

    try {
      setIsLoading(true);
      setError(null);
      setCurrentPage(1);
      const response = await relatorioGestaoRecisoes(payload);
      setDados(
        Array.isArray(response)
          ? response
          : response && Array.isArray(response.data)
            ? response.data
            : []
      );
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, [dataInicial, dataFinal]);

  return (
    <>
      <Header title="Gestão de Rescisões" />
      <div className="gestao-rescisoes container-fluid bg-white p-3">
        <Row className="align-items-center">
          <Col md={"auto"}>
            <Form.Group>
              <Form.Label>Data Inicial</Form.Label>
              <DateMask allowEmpty>
                {() => (
                  <div className="position-relative">
                    <MaskedInput
                      name="data_inicial"
                      guide={false}
                      className={`form-control ${dataPagamentoInicialError ? 'is-invalid' : ''
                        }`}
                      placeholder="Ex.: 01/01/2019"
                      mask={constants.MASK_DATE}
                      value={dataInicial}
                      onChange={e =>
                        setDataPagamentoInicial(e.currentTarget.value)
                      }
                    />
                    <Form.Control.Feedback className="d-flex" type="invalid">
                      {dataPagamentoInicialError}
                    </Form.Control.Feedback>
                  </div>
                )}
              </DateMask>
            </Form.Group>
          </Col>
          <Col md={"auto"}>
            <Form.Group>
              <Form.Label>Data Final</Form.Label>
              <DateMask allowEmpty>
                {() => (
                  <div className="position-relative">
                    <MaskedInput
                      name="data_pagamento_final"
                      guide={false}
                      className={`form-control ${dataPagamentoFinalError ? 'is-invalid' : ''
                        }`}
                      placeholder="Ex.: 01/01/2019"
                      mask={constants.MASK_DATE}
                      value={dataFinal}
                      onChange={e =>
                        setDataPagamentoFinal(e.currentTarget.value)
                      }
                    />
                    <Form.Control.Feedback className="d-flex" type="invalid">
                      {dataPagamentoFinalError}
                    </Form.Control.Feedback>
                  </div>
                )}
              </DateMask>
            </Form.Group>
          </Col>
          <Col className="d-flex justify-content-end align-items-end">
            <Button
              className="mt-3 mr-2"
              variant="primary"
              disabled={!canSearch || isLoading}
              onClick={handleSearch}
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mr-2"
                  />
                  Buscando...
                </>
              ) : (
                <>
                  <FaSearch className="mr-2" />
                  Buscar
                </>
              )}
            </Button>
            <Button
              variant="success"
              type="submit"
              className="mt-3"
              disabled={isLoadingExcel || !dados.length}
              onClick={async () => {
                try {
                  setIsLoadingExcel(true);
                  await salvarEmExcel(
                    dados,
                    '/exportador/excel/relatorio-gestao-rescisoes'
                  );
                } catch (error) {
                  console.error(error);
                } finally {
                  setIsLoadingExcel(false);
                }
              }}
            >
              <FaFileExcel />
              <span className="ml-2 ocultar-mobile">
                {isLoadingExcel ? 'Salvando...' : 'Salvar'}
              </span>
            </Button>
          </Col>
        </Row>

        {error && (
          <Row className="mt-3">
            <Col md={12}>
              <Alert variant="danger">
                Falha ao carregar dados de Recisões.
              </Alert>
            </Col>
          </Row>
        )}

        {!isLoading && dados.length > 0 && (
          <>
            <Row className="mt-3">
              <Col md={6}>
                <Card className="shadow-sm">
                  <Card.Header className="bg-white">
                    <strong>Valores a pagar</strong>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <h3 className="mb-0">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(totalAPagar || 0)}
                    </h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="shadow-sm">
                  <Card.Header className="bg-white">
                    <strong>Valores pagos</strong>
                  </Card.Header>
                  <Card.Body className="d-flex align-items-center justify-content-center">
                    <h3 className="mb-0">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      }).format(totalPago || 0)}
                    </h3>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col>
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-white">
                    <strong>Percentual de pagamentos do período</strong>
                  </Card.Header>
                  <Card.Body className="d-flex justify-content-center">
                    <div style={{ width: '320px', maxWidth: '100%' }}>
                      <Pie data={pieData} options={pieOptions} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Card className="h-100 shadow-sm">
                  <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                    <strong>Tabela de dados</strong>
                    <small className="text-muted">
                      {dados.length} registro(s) encontrado(s)
                    </small>
                  </Card.Header>
                  <Card.Body className="p-2">
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered table-hover table-sm">
                        <thead>
                          <tr className="text-center">
                            <th>Contrato</th>
                            <th>Data Rescisão</th>
                            <th>Usuário</th>
                            <th>Conta</th>
                            <th>GE</th>
                            <th>Quadra</th>
                            <th>Lote</th>
                            <th>Empreendimento</th>
                            <th>SPE</th>
                            <th>Data Efetivação</th>
                            <th>Valor</th>
                            <th>Pago no período</th>
                            <th>A pagar no período</th>
                          </tr>
                        </thead>
                        <tbody className="text-center">
                          {paginatedData.map((item, idx) => (
                            <tr key={idx}>
                              <td>{item.contrato}</td>
                              <td>
                                {item && item.data_recisao
                                  ? createDate(
                                    item.data_recisao,
                                    constants.DATE_FORMAT_SERVER
                                  ).format(constants.DATE_DEFAULT_FORMAT)
                                  : '-'}
                              </td>
                              <td>{item.usuario_recisao || '-'}</td>
                              <td>{item.codigo_conta}</td>
                              <td>{item.ge}</td>
                              <td>{item.quadra}</td>
                              <td>{item.lote}</td>
                              <td>{item.empreendimento}</td>
                              <td>{item.spe}</td>
                              <td>
                                {item && item.data_efetivacao
                                  ? createDate(
                                    item.data_efetivacao,
                                    constants.DATE_FORMAT_SERVER
                                  ).format(constants.DATE_DEFAULT_FORMAT)
                                  : '-'}
                              </td>
                              <td>
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }).format(item.valor_da_venda || 0)}
                              </td>
                              <td>
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }).format(item.valor_pago_periodo || 0)}
                              </td>
                              <td>
                                {new Intl.NumberFormat('pt-BR', {
                                  style: 'currency',
                                  currency: 'BRL',
                                }).format(item.valor_a_pagar_periodo || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center p-3 border-top">
                        <small className="text-muted">
                          Página {currentPage} de {totalPages}
                        </small>
                        <Pagination className="mb-0">
                          <Pagination.First
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                          />
                          <Pagination.Prev
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          />
                          {[...Array(totalPages)].map((_, index) => {
                            const page = index + 1;
                            if (
                              page === 1 ||
                              page === totalPages ||
                              (page >= currentPage - 2 && page <= currentPage + 2)
                            ) {
                              return (
                                <Pagination.Item
                                  key={page}
                                  active={page === currentPage}
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </Pagination.Item>
                              );
                            } else if (
                              page === currentPage - 3 ||
                              page === currentPage + 3
                            ) {
                              return <Pagination.Ellipsis key={page} disabled />;
                            }
                            return null;
                          })}
                          <Pagination.Next
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          />
                          <Pagination.Last
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                          />
                        </Pagination>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>
    </>
  );
}
